import axios from "axios";
import { addQueryParam, areArraysEqual } from "./utils.js";

import { load_explorer_swanbot_key, swanbot_URL } from "./api.js";
import { isSwanbotRelayEnabled } from "./swanbot_relay.js";

/* Polling logic */

const DATAEXPLORER_KEEPALIVE_INTERVAL_MS = 60000; // every 60 seconds
const DATAEXPLORER_KEEPALIVE_MAX = 180; // 3 hours
const DATAEXPLORER_KEEPALIVE_MAX_DURATION_MS = DATAEXPLORER_KEEPALIVE_INTERVAL_MS * DATAEXPLORER_KEEPALIVE_MAX;

let dataExplorerActiveRequestCount = 0;
let dataExplorerKeepAliveIntervalId = null;
let dataExplorerKeepAliveCount = 0;
let dataExplorerKeepAliveFailCount = 0;
let dataExplorerKeepAliveInFlight = false;
let dataExplorerKeepAliveMaxLocalDate = null;
let dataExplorerReleasedSessionNotification = null;
let dataExplorerStatusTransitionTimeoutId = null;

function normalizeDataExplorerStatus(status) {
	if (typeof status !== 'string') {
		return '';
	}

	const normalizedStatus = status.trim();
	if (!normalizedStatus || normalizedStatus.toLowerCase() === 'done') {
		return '';
	}
	if (normalizedStatus.toLowerCase() === 'set up your session') {
		return 'Setting up your session';
	}
	return normalizedStatus;
}

function getCurrentDataExplorerStatus(statusList) {
	if (!Array.isArray(statusList)) {
		return '';
	}
	return statusList.reduceRight((currentStatus, status) => (
		currentStatus || normalizeDataExplorerStatus(status)
	), '');
}

function isSwanbotRequest(error) {
	const requestUrl = error && error.config && error.config.url;
	if (isSwanbotRelayEnabled() && typeof requestUrl === 'string' && requestUrl.startsWith('/xapi/swanbot/')) {
		return true;
	}
	const configuredSwanbotUrl = window.WingmanState && window.WingmanState.swanbot_URL;
	const swanbotBaseUrl = configuredSwanbotUrl || swanbot_URL;
	return typeof requestUrl === 'string' && !!swanbotBaseUrl && requestUrl.startsWith(swanbotBaseUrl);
}

function isDataExplorerKeepAliveRequest(error) {
	const requestUrl = error && error.config && error.config.url;
	if (typeof requestUrl !== 'string') {
		return false;
	}

	try {
		return new URL(requestUrl, window.location.origin).pathname.endsWith('/heatmap/extendSession');
	} catch {
		return false;
	}
}

function handleDataExplorerReleasedSession(error) {
	const response = error && error.response;
	const sessionReleased = response && (response.status === 410 || (response.status === 404 && isDataExplorerKeepAliveRequest(error)));
	if (!isSwanbotRequest(error) || !sessionReleased) {
		return false;
	}

	const releasedSession = window.WingmanState && window.WingmanState.swanbot_dataexplorer_session;
	setDataExplorerSwanbotSession('');

	if (releasedSession && dataExplorerReleasedSessionNotification !== releasedSession) {
		dataExplorerReleasedSessionNotification = releasedSession;
		if (typeof window.send_dataexplorer_results === 'function') {
			void window.send_dataexplorer_results('Your session has ended.', 'swanbot_start', 0);
		}
	}

	return true;
}

axios.interceptors.response.use(
	response => response,
	error => {
		handleDataExplorerReleasedSession(error);
		return Promise.reject(error);
	}
);

export function consumeDataExplorerReleasedSession() {
	if (!dataExplorerReleasedSessionNotification) {
		return false;
	}

	dataExplorerReleasedSessionNotification = null;
	return true;
}

function isDataExplorerView() {
	return window.location.pathname.startsWith('/dataexplorer');
}

function clearDataExplorerKeepAliveLoop() {
	if (dataExplorerKeepAliveIntervalId) {
		clearInterval(dataExplorerKeepAliveIntervalId);
		dataExplorerKeepAliveIntervalId = null;
	}
}

function recordDataExplorerKeepAliveFailure() {
	dataExplorerKeepAliveFailCount += 1;
	if (dataExplorerKeepAliveFailCount < 4) {
		return false;
	}

	clearDataExplorerKeepAliveLoop();
	window.WingmanState.swanbot_dataexplorer_session = null;
	return true;
}

function getDataExplorerKeepAliveMaxLocalDate(baseDate = new Date()) {
	return new Date(baseDate.getTime() + DATAEXPLORER_KEEPALIVE_MAX_DURATION_MS);
}

function refreshDataExplorerKeepAliveMaxLocalDate(baseDate = new Date()) {
	dataExplorerKeepAliveMaxLocalDate = getDataExplorerKeepAliveMaxLocalDate(baseDate);
}

function isDataExplorerKeepAlivePastHardLimit(referenceDate = new Date()) {
	return !!dataExplorerKeepAliveMaxLocalDate && referenceDate >= dataExplorerKeepAliveMaxLocalDate;
}

function getCurrentCustomerId() {
	const customerInput = document.querySelector("#customer_id");
	return customerInput ? customerInput.value : "";
}

function buildDataExplorerKeepAliveUrl(sessionId) {
	const baseUrl = (window.WingmanState.swanbot_URL || swanbot_URL).replace(/\/+$/, '');
	const customerId = getCurrentCustomerId();
	const keepAlivePath = customerId ? '/' + customerId + '/heatmap/extendSession' : '/heatmap/extendSession';
	let keepAliveUrl = addQueryParam(baseUrl + keepAlivePath, 'session', sessionId);

	if (window.WingmanState && window.WingmanState.swanbot_key) {
		keepAliveUrl = addQueryParam(keepAliveUrl, 'key', window.WingmanState.swanbot_key);
	}

	return keepAliveUrl;
}

async function sendDataExplorerKeepAlive() {
	const sessionId = window.WingmanState && window.WingmanState.swanbot_dataexplorer_session;

	if (!isDataExplorerView()) {
		clearDataExplorerKeepAliveLoop();
		return;
	}

	if (!sessionId) {
		clearDataExplorerKeepAliveLoop();
		return;
	}

	if (dataExplorerActiveRequestCount > 0 || dataExplorerKeepAliveInFlight) {
		return;
	}

	if (dataExplorerKeepAliveCount >= DATAEXPLORER_KEEPALIVE_MAX || isDataExplorerKeepAlivePastHardLimit()) {
		clearDataExplorerKeepAliveLoop();
		return;
	}

	if ((!window.WingmanState || !window.WingmanState.swanbot_key) && !isSwanbotRelayEnabled()) {
		try {
			await load_explorer_swanbot_key();
		} catch (error) {
			console.error('Data Explorer keep-alive could not load Swanbot key:', error);
		}
	}

	if ((!window.WingmanState || !window.WingmanState.swanbot_key) && !isSwanbotRelayEnabled()) {
		return;
	}

	dataExplorerKeepAliveInFlight = true;

	try {
		const response = await axios.get(buildDataExplorerKeepAliveUrl(sessionId), { withCredentials: true });
		if (response.data && response.data.extended === false) {
			if (recordDataExplorerKeepAliveFailure()) {
				return;
			}
		} else {
			dataExplorerKeepAliveFailCount = 0;
		}
		dataExplorerKeepAliveCount += 1;
	} catch (error) {
		console.error('Data Explorer keep-alive failed:', error);
		recordDataExplorerKeepAliveFailure();
	} finally {
		dataExplorerKeepAliveInFlight = false;

		if (!window.WingmanState || !window.WingmanState.swanbot_dataexplorer_session || dataExplorerKeepAliveCount >= DATAEXPLORER_KEEPALIVE_MAX || isDataExplorerKeepAlivePastHardLimit()) {
			clearDataExplorerKeepAliveLoop();
		}
	}
}

function ensureDataExplorerKeepAliveLoop() {
	if (dataExplorerKeepAliveIntervalId) {
		return;
	}

	if (!isDataExplorerView()) {
		clearDataExplorerKeepAliveLoop();
		return;
	}

	if (!window.WingmanState || !window.WingmanState.swanbot_dataexplorer_session) {
		return;
	}

	if (dataExplorerActiveRequestCount > 0 || dataExplorerKeepAliveCount >= DATAEXPLORER_KEEPALIVE_MAX || isDataExplorerKeepAlivePastHardLimit()) {
		return;
	}

	dataExplorerKeepAliveIntervalId = setInterval(function () {
		void sendDataExplorerKeepAlive();
	}, DATAEXPLORER_KEEPALIVE_INTERVAL_MS);
}

export function resetDataExplorerKeepAliveCounter() {
	dataExplorerKeepAliveCount = 0;
	dataExplorerKeepAliveFailCount = 0;
	refreshDataExplorerKeepAliveMaxLocalDate();
	clearDataExplorerKeepAliveLoop();
	dataExplorerKeepAliveInFlight = false;
}

export function setDataExplorerSwanbotSession(sessionId) {
	if (!window.WingmanState) {
		return;
	}

	const previousSessionId = window.WingmanState.swanbot_dataexplorer_session || "";
	const normalizedSessionId = sessionId || "";
	window.WingmanState.swanbot_dataexplorer_session = normalizedSessionId;
	if (normalizedSessionId && normalizedSessionId !== dataExplorerReleasedSessionNotification) {
		dataExplorerReleasedSessionNotification = null;
	}

	if (!window.WingmanState.swanbot_dataexplorer_session) {
		dataExplorerKeepAliveMaxLocalDate = null;
		clearDataExplorerKeepAliveLoop();
		return;
	}

	if (!dataExplorerKeepAliveMaxLocalDate || normalizedSessionId !== previousSessionId) {
		refreshDataExplorerKeepAliveMaxLocalDate();
	}

	if (!isDataExplorerView()) {
		clearDataExplorerKeepAliveLoop();
		return;
	}

	if (dataExplorerActiveRequestCount === 0) {
		ensureDataExplorerKeepAliveLoop();
	}
}

export function trackDataExplorerSwanbotRequest(monitoredPromise, options = {}) {
	if (!monitoredPromise || typeof monitoredPromise.finally !== 'function') {
		return monitoredPromise;
	}

	if (options.resetKeepAliveCounter) {
		resetDataExplorerKeepAliveCounter();
	} else {
		clearDataExplorerKeepAliveLoop();
	}

	dataExplorerActiveRequestCount += 1;

	monitoredPromise.finally(() => {
		dataExplorerActiveRequestCount = Math.max(0, dataExplorerActiveRequestCount - 1);

		if (dataExplorerActiveRequestCount === 0 && window.WingmanState && window.WingmanState.swanbot_dataexplorer_session) {
			ensureDataExplorerKeepAliveLoop();
		}
	});

	return monitoredPromise;
}

function getInlineStatusChange(previousStatus, currentStatus) {
	if (!previousStatus || !currentStatus || previousStatus === currentStatus) {
		return null;
	}

	let prefixLength = 0;
	const sharedLength = Math.min(previousStatus.length, currentStatus.length);
	while (prefixLength < sharedLength && previousStatus[prefixLength] === currentStatus[prefixLength]) {
		prefixLength += 1;
	}

	let previousSuffixStart = previousStatus.length;
	let currentSuffixStart = currentStatus.length;
	while (
		previousSuffixStart > prefixLength
		&& currentSuffixStart > prefixLength
		&& previousStatus[previousSuffixStart - 1] === currentStatus[currentSuffixStart - 1]
	) {
		previousSuffixStart -= 1;
		currentSuffixStart -= 1;
	}

	while (
		prefixLength > 0
		&& !/\s/.test(previousStatus[prefixLength - 1])
		&& !/\s/.test(currentStatus[prefixLength - 1])
	) {
		prefixLength -= 1;
	}

	while (
		previousSuffixStart < previousStatus.length
		&& currentSuffixStart < currentStatus.length
		&& !/\s/.test(previousStatus[previousSuffixStart])
		&& !/\s/.test(currentStatus[currentSuffixStart])
	) {
		previousSuffixStart += 1;
		currentSuffixStart += 1;
	}

	const prefix = currentStatus.slice(0, prefixLength);
	const previousText = previousStatus.slice(prefixLength, previousSuffixStart);
	const currentText = currentStatus.slice(prefixLength, currentSuffixStart);
	const suffix = currentStatus.slice(currentSuffixStart);
	const unchangedLength = prefix.length + suffix.length;
	const longestLength = Math.max(previousStatus.length, currentStatus.length);
	const changedLength = Math.max(previousText.length, currentText.length);

	if (!previousText || !currentText || changedLength > 24 || unchangedLength < longestLength * 0.6) {
		return null;
	}

	return { prefix, previousText, currentText, suffix };
}

export function renderDataExplorerStatus(statusList, lastStatus = []) {
	if (!Array.isArray(statusList) || statusList.length === 0 || areArraysEqual(statusList, lastStatus)) {
		return lastStatus;
	}

	const latestStatus = [...statusList];
	const currentStatus = getCurrentDataExplorerStatus(latestStatus);
	const previousStatus = getCurrentDataExplorerStatus(lastStatus);
	if (!currentStatus || currentStatus === previousStatus) {
		return latestStatus;
	}

	const statusElement = document.getElementById('swanbot_loading_status');
	if (!statusElement) {
		console.warn("Element with ID 'swanbot_loading_status' not found in the DOM.");
		return latestStatus;
	}

	if (dataExplorerStatusTransitionTimeoutId) {
		clearTimeout(dataExplorerStatusTransitionTimeoutId);
		dataExplorerStatusTransitionTimeoutId = null;
	}

	statusElement.classList.remove('status-fade-in', 'status-fade-out');
	const displayedStatus = statusElement.textContent || previousStatus;
	const inlineChange = getInlineStatusChange(displayedStatus, currentStatus);
	if (inlineChange) {
		const changedText = document.createElement('span');
		changedText.className = 'status-inline-fade-out';
		changedText.textContent = inlineChange.previousText;
		statusElement.replaceChildren(
			document.createTextNode(inlineChange.prefix),
			changedText,
			document.createTextNode(inlineChange.suffix)
		);

		dataExplorerStatusTransitionTimeoutId = setTimeout(() => {
			changedText.textContent = inlineChange.currentText;
			changedText.className = 'status-inline-fade-in';
			dataExplorerStatusTransitionTimeoutId = setTimeout(() => {
				statusElement.textContent = currentStatus;
				dataExplorerStatusTransitionTimeoutId = null;
			}, 240);
		}, 160);

		return latestStatus;
	}

	const showCurrentStatus = () => {
		statusElement.textContent = currentStatus;
		statusElement.classList.remove('status-fade-out');
		statusElement.classList.add('status-fade-in');
		dataExplorerStatusTransitionTimeoutId = setTimeout(() => {
			statusElement.classList.remove('status-fade-in');
			dataExplorerStatusTransitionTimeoutId = null;
		}, 240);
	};

	if (statusElement.textContent.trim()) {
		statusElement.classList.add('status-fade-out');
		dataExplorerStatusTransitionTimeoutId = setTimeout(showCurrentStatus, 160);
	} else {
		showCurrentStatus();
	}

	return latestStatus;
}

export function monitorRequestAndPoll(monitoredPromise, initialDelayMs, pollIntervalMs) {
	let pollIntervalId = null;
	let lastStatus = [];
	let customerid = getCurrentCustomerId();
	let statusurl = (window.WingmanState.swanbot_URL || swanbot_URL) + customerid + '/whatamidoing';

	if (window.WingmanState.swanbot_dataexplorer_session) {
		statusurl = statusurl + '?session=' + window.WingmanState.swanbot_dataexplorer_session;

		if (window.WingmanState.swanbot_key) {
			statusurl = addQueryParam(statusurl, 'key', window.WingmanState.swanbot_key);
		}

		statusurl = addQueryParam(statusurl, 'format', 'data');


		// Function to start the polling
		const startPolling = () => {
			// Make an initial status call and then set up the interval for subsequent calls
			const pollStatus = () => {
				axios.get(statusurl, { withCredentials: true })
					.then(response => {
						if (response.data && response.data.whatamidoing && response.data.whatamidoing.length > 0) {
							lastStatus = renderDataExplorerStatus(response.data.whatamidoing, lastStatus);
						}

					})
					.catch(error => {
						// Log any errors during polling, but don't stop the main loop
						// The main loop will be stopped by the monitoredPromise's .finally()
						lastStatus = [];
						console.error("Error during status poll:", error);
					});
			};

			// Start the first poll
			pollStatus();

			// Set up the recurring interval
			pollIntervalId = setInterval(pollStatus, pollIntervalMs);
		};

		// Start the initial timeout to begin polling after the specified delay
		const initialTimeoutId = setTimeout(() => {
			startPolling();
		}, initialDelayMs);

		// Monitor the main promise and clear all polling when it resolves or rejects
		monitoredPromise
			.then(response => {
				lastStatus = [];
				//console.log("Main request completed successfully.");
				// You can process the final response here if needed
				return response; // Pass the response along the chain
			})
			.catch(error => {
				lastStatus = [];
				console.error("Main request encountered an error:", error);
				// Pass the error along to the original catch block
				throw error;
			})
			.finally(() => {
				lastStatus = [];
				//console.log("Main request finished. Stopping polling.");
				// Clear the initial timeout in case it hasn't fired yet
				clearTimeout(initialTimeoutId);
				// Clear the polling interval
				if (pollIntervalId) {
					clearInterval(pollIntervalId);
				}
			});
	} else {
		//console.log("no session yet")
	}
}

